import { Types } from "../actionTypes";
import { createSelector } from "@reduxjs/toolkit";
import { Member, ROUTES, AuthModalTypes } from "../../constants";
import { signUpSteps } from "@Src/components/Auth/authHelpers";
import { t } from "@lingui/core/macro";
const {
  REGISTERING,
  REGISTRATION_FAILURE,
  REGISTRATION_SUCCESSFUL,
  EMAIL_IS_AVAILABLE,
  AUTHENTICATING,
  UNAUTHENTICATING,
  AUTHENTICATION_FAILURE,
  AUTHENTICATION_SUCCESSFUL,
  UNAUTHENTICATING_SUCCESSFUL,
  UNAUTHENTICATING_FAILURE,
  LOADING,
  LOAD_FAILURE,
  LOAD_SUCCESS,
  SAVE_FAILURE,
  SAVE_SUCCESS,
  SAVING,
  VALIDATING_SESSION,
  SIGN_IN_SUCCESSFUL,
  COMPLETING_SETUP,
  COMPLETING_SETUP_SUCCESSFUL,
  COMPLETING_SETUP_FAILURE,
  SHARED_SECRET_INVALID,
  REGISTRATION_CODE_VERIFIED,
  MEMBER_ID_VERIFIED,
  NEEDS_SHARED_SECRET,
  INVALID_CAPTCHA,
} = Member;

export const initialState = {
  member: {
    member_key: "guest", // Default value needed for categories calls
    member_id: "guest",
    uid: null,
    first_name: null,
    last_name: null,
    email: null,
    admin: false,
    cvtStatus: null,
    has_registered: null,
    has_password: null,
  },
  isLoading: false,
  authentication: {
    isAuthOpen: false,
    screen: AuthModalTypes.SIGN_IN,
    signUpStep: signUpSteps.GENERAL,
    status: null, // [null, REGISTERING, AUTHENTICATING, UNAUTHENTICATING, SUCCESSFUL, FAILURE]
    statusText: null, // [null, 'something determined by product team']
    error: null, // assign error object from server to this
  },
  memberAccount: null,
  memberAccountStatus: {
    status: null, // [null, LOADING, SAVING, LOAD_SUCCESS, SAVE_SUCCESS, LOAD_FAILURE, SAVE_FAILURE ]
    statusText: null, // [null, 'some message determined by product team']
  },
  directedParams: null,
  smsAndZip: {
    setupLater: null,
  },
};

function member(state = initialState, action) {
  const { type } = action;
  switch (type) {
    case Types.COMPLETE_ACCOUNT_SETUP:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: COMPLETING_SETUP,
          statusText: null,
          error: null,
        },
      };
    case Types.COMPLETE_ACCOUNT_SETUP_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: COMPLETING_SETUP_SUCCESSFUL,
          statusText: t`Completion of account setup was successful.`,
          error: null,
        },
        member: action.data,
      };
    case Types.COMPLETE_ACCOUNT_SETUP_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: COMPLETING_SETUP_FAILURE,
          statusText: t`Completion of account setup failed.`,
          error: action.error,
        },
      };
    case Types.SET_AUTH_OPEN:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          isAuthOpen: action.open,
          screen: action.screen ? action.screen : state.authentication.screen,
        },
      };
    case Types.SET_AUTH_SCREEN:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          screen: action.payload,
        },
      };
    case Types.RESET_AUTH:
      return {
        ...state,
        authentication: {
          ...initialState.authentication,
          status: state.authentication.status,
          isAuthOpen: state.authentication.isAuthOpen,
        },
      };

    case Types.CHECK_EMAIL_AVAILABILITY:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTERING,
          statusText: t`Checking email availability.`,
          error: null,
        },
      };
    case Types.CHECK_EMAIL_AVAILABILITY_SUCCESS:
      const { isEmailExists } = action.data;
      const status = isEmailExists ? EMAIL_IS_AVAILABLE : NEEDS_SHARED_SECRET;
      const statusText = isEmailExists
        ? t`Email is found.`
        : t`Email is not found, needs shared secret.`;
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: status,
          statusText: statusText,
          error: null,
        },
      };
    case Types.CHECK_EMAIL_AVAILABILITY_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_FAILURE,
          statusText: t`Check email availability failure.`,
          error: action.error.error_code,
          errorMsg: action.error.error_message,
        },
      };
    case Types.CHECK_REGISTRATION_CODE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTERING,
          statusText: t`Checking registration code.`,
          error: null,
        },
      };
    case Types.CHECK_REGISTRATION_CODE_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_CODE_VERIFIED,
          statusText: t`Registration code verified.`,
          error: null,
        },
      };
    case Types.CHECK_REGISTRATION_CODE_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_FAILURE,
          statusText: t`Check registration code failure.`,
          error: action.error.error_code,
          errorMsg: action.error.error_message,
        },
      };
    case Types.CHECK_MEMBER_ID:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTERING,
          statusText: t`Checking member id.`,
          error: null,
        },
      };
    case Types.CHECK_MEMBER_ID_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: MEMBER_ID_VERIFIED,
          statusText: t`Member id verified.`,
          error: null,
        },
      };
    case Types.CHECK_MEMBER_ID_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_FAILURE,
          statusText: t`Check member id failure.`,
          error: action.error.error_code,
          errorMsg: action.error.error_message,
        },
      };
    case Types.CREATE_MEMBER:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTERING,
          statusText: t`Registering member.`,
          error: null,
        },
      };
    case Types.CREATE_MEMBER_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_SUCCESSFUL,
          statusText: t`Successfully created member.`,
          error: null,
        },
        member: action.data,
      };
    case Types.CREATE_MEMBER_FAILURE:
      const { error_code } = action.error;
      const regStatus = error_code === INVALID_CAPTCHA ? INVALID_CAPTCHA : REGISTRATION_FAILURE;
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: regStatus,
          statusText: t`Failed to create member.`,
          error: action.error.error_code,
          errorMsg: action.error.error_message,
        },
      };
    case Types.SIGN_IN:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: AUTHENTICATING,
          statusText: t`Signing in.`,
          error: null,
        },
      };
    case Types.AUTH_BY_CVT:
    case Types.AUTH_BY_SSO_TOKEN:
    case Types.AUTH_BY_SLA_TOKEN:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: AUTHENTICATING,
          statustext: t`Signing in.`,
          error: null,
        },
      };
    case Types.SIGN_IN_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: SIGN_IN_SUCCESSFUL,
          statusText: t`Successfully signed in.`,
          error: null,
        },
        member: action.data,
      };
    case Types.SIGN_IN_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: AUTHENTICATION_FAILURE,
          statusText: t`Failed to sign in.`,
          error: action.error,
        },
        member: {
          ...initialState.member,
        },
      };
    case Types.SIGN_OUT:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: UNAUTHENTICATING,
          statusText: t`Signing out.`,
          destinationRoute: action.route ? action.route : ROUTES.HOME_PATH,
          error: null,
        },
      };
    case Types.SIGN_OUT_SUCCESS:
      return {
        ...initialState,
        authentication: {
          ...state.authentication,
          status: UNAUTHENTICATING_SUCCESSFUL,
          statusText: t`Successfully signed out.`,
          error: null,
        },
      };
    case Types.SIGN_OUT_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: UNAUTHENTICATING_FAILURE,
          statusText: t`Failed to sign out.`,
          error: action.error,
        },
      };
    case Types.VALIDATE_SESSION:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: VALIDATING_SESSION,
          statusText: t`Validating existing session.`,
          error: null,
        },
      };
    case Types.VALIDATE_SESSION_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: AUTHENTICATION_SUCCESSFUL,
          statusText: t`Session is valid.`,
          error: null,
        },
        member: action.data,
        memberAccount: null,
        memberAccountStatus: {
          status: LOADING,
          statusText: null,
        },
      };
    case Types.VALIDATE_SESSION_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: AUTHENTICATION_FAILURE,
          statusText: t`Session is not valid.`,
        },
      };
    case Types.FETCH_MEMBER_ACCOUNT:
      return {
        ...state,
        memberAccount: null,
        memberAccountStatus: {
          status: LOADING,
          statusText: null,
        },
      };
    case Types.FETCH_MEMBER_ACCOUNT_SUCCESS:
      return {
        ...state,
        memberAccount: action.data,
        memberAccountStatus: {
          status: LOAD_SUCCESS,
          statusText: null,
        },
      };
    case Types.FETCH_MEMBER_ACCOUNT_FAILURE:
      return {
        ...state,
        memberAccountStatus: {
          status: LOAD_FAILURE,
          statusText: t`Unable to retrieve member account info`,
        },
      };
    case Types.UPDATE_MEMBER_ACCOUNT:
      return {
        ...state,
        memberAccountStatus: {
          status: SAVING,
          statusText: null,
        },
      };
    case Types.UPDATE_MEMBER_ACCOUNT_SUCCESS:
      return {
        ...state,
        memberAccount: action.data,
        memberAccountStatus: {
          status: SAVE_SUCCESS,
          statusText: null,
        },
      };
    case Types.UPDATE_MEMBER_ACCOUNT_FAILURE:
      return {
        ...state,
        memberAccountStatus: {
          status: SAVE_FAILURE,
          statusText: t`Unable to save member account info`,
        },
      };
    case Types.COMPLETE_MEMBER_PROFILE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTERING,
          statusText: t`Completing member profile.`,
          error: null,
        },
      };
    case Types.COMPLETE_MEMBER_SUCCESS:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_SUCCESSFUL,
          statusText: t`Successfully completed member.`,
          error: null,
        },
        member: action.data,
      };
    case Types.COMPLETE_MEMBER_FAILURE:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: REGISTRATION_FAILURE,
          statusText: t`Failed to complete member.`,
          error: action.error.error_code,
          errorMsg: action.error.error_message,
        },
      };
    case Types.SET_SIGN_UP_STEP:
      return {
        ...state,
        authentication: {
          ...state.authentication,
          status: null,
          statusText: null,
          error: null,
          signUpStep: action.payload,
        },
      };
    case Types.SET_DIRECTED_PARAMS:
      return {
        ...state,
        directedParams: action.payload,
      };

    case Types.FETCH_MEMBER_DEFAULT_LOCATION:
      return {
        ...state,
        memberAccount: null,
        memberAccountStatus: {
          status: LOADING,
          statusText: null,
        },
      };

    case Types.FETCH_MEMBER_DEFAULT_LOCATION_SUCCESS:
      return {
        ...state,
        memberAccount: {
          ...state.memberAccount,
          preferences: action.data,
        },
        memberAccountStatus: {
          status: LOAD_SUCCESS,
          statusText: null,
        },
      };

    case Types.FETCH_MEMBER_DEFAULT_LOCATION_FAILURE:
      return {
        ...state,
        memberAccountStatus: {
          status: LOAD_FAILURE,
          statusText: t`Unable to retrieve member location`,
        },
      };

    case Types.SHOW_SETUP_LATER_NOTIFICATION:
      const setupLater = action.setupLater;
      return {
        ...state,
        smsAndZip: {
          setupLater: setupLater,
        },
      };
    default:
      return state;
  }
}

export const getFirstName = (state) => state.member.first_name;
export const getLastName = (state) => state.member.last_name;
export const isMemberAdmin = (state) => state.member.admin;
export const hasAuthenticatedUser2 = createSelector([(state) => state.member], (member) => {
  return member && member.uid !== null;
});
export const hasAuthenticatedUser = (state) => state.member && state.member.uid !== null;
// Uid and Cvt are the same  - TODO: Rename Uid to Cvt since that seems to be the direction as of the time of this comment
export const getUid = (state) => state.member.uid;
export const getMember = (state) => state.member;
export const getMemberCvtStatus = (state) => state.member.cvtStatus;
export const getIsMemberLoading = (state) => state.isLoading;
export const getRegistrationStatus = (state) => state.authentication.status;
export const getRegistrationStatusText = (state) => state.authentication.statusText;
export const getRegistrationError = (state) => {
  if (state.authentication.errorMsg !== SHARED_SECRET_INVALID) {
    return state.authentication.error;
  } else {
    return state.authentication.errorMsg;
  }
};
export const getAuthenticationDestination = (state) => state.authentication.destinationRoute;
export const getAuthenticationStatus = (state) => state.authentication.status;
export const getAuthenticationStatusText = (state) => state.authentication.statusText;
export const getAuthenticationError = (state) => state.authentication.error;
export const getMemberAccountInfo = (state) => state.memberAccount;
export const getMemberAccountInfoStatus = (state) => state.memberAccountStatus;
export const getMemberDefaultLocation = (state) => {
  if (
    state.memberAccount === null ||
    !state.memberAccount.preferences.default_location ||
    state.memberAccount.preferences.default_location.name === null ||
    state.memberAccount.preferences.default_location.name === ""
  ) {
    return null;
  }
  return state.memberAccount.preferences.default_location;
};
export const getUseMemberDefaultLocation = (state) => {
  if (state.memberAccount === null) {
    return null;
  }
  return state.memberAccount.preferences.use_default_location_on_login;
};
export const getAddOnsMemberAccountInfo = createSelector([(state) => state.member], (member) => {
  if (member.uid === null) {
    return {
      member_key: "",
      member_id: "",
      first_name: "",
      last_name: "",
    };
  }
  const { member_key, member_id, first_name, last_name } = member;
  return { member_key, member_id, first_name, last_name };
});

export const getAuthenticationIsOpen = (state) => state.authentication.isAuthOpen;
export const getAuthenticationScreen = (state) => state.authentication.screen;
export const getSignUpStep = (state) => state.authentication.signUpStep;
export const getDirectedParams = (state) => state.directedParams;
export const getSmsZipSetupLaterFlag = (state) => state.smsAndZip.setupLater;

export default member;
